import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircle, RotateCcw, Camera, Globe2, FileSpreadsheet, Car,
  Gauge, Users, Landmark, ArrowRight, Check, ShieldCheck, Sparkles,
  Loader2, CheckCircle2, Calendar, Tag, Building2, Receipt, Navigation, MapPin, Route as RouteIcon,
  Upload, Table2, Download, Filter, ListChecks, FileDown, LayoutGrid,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LandingHeader } from '../components/LandingHeader';
import Seo from '../components/Seo';
import { useCountry } from '../contexts/CountryContext';
import { WHATSAPP_LINK } from '../utils/constants';

/* ---------- helpers ---------- */
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const h = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', h);
    return () => mq.removeEventListener?.('change', h);
  }, []);
  return reduced;
};

function useCountUp(target, duration = 700) {
  const [val, setVal] = useState(target);
  const prev = useRef(target);
  useEffect(() => {
    const from = prev.current;
    const to = target;
    prev.current = target;
    if (from === to) { setVal(to); return; }
    let raf, start;
    const tick = (t) => {
      if (start == null) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(from + (to - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const money = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
};

/* ---------- chat demo ---------- */
const SEQUENCE = [
  { from: 'user', text: 'Got paid $1,200 from Acme Design', income: 1200 },
  { from: 'bot', text: 'Income logged: $1,200 → Client revenue. Nice one!' },
  { from: 'user', text: 'Spent $45 on lunch with a client' },
  { from: 'bot', text: '$45 → Meals (50% deductible). Saved.', ded: 45 },
  { from: 'user', text: '$60 on gas' },
  { from: 'bot', text: '$60 → Car & truck expenses. Logged ✓', ded: 60 },
  { from: 'user', text: '[Receipt photo]', receipt: true },
  { from: 'bot', text: 'Scanned: Staples — $128.40 → Office supplies. Categorized ✓', ded: 128.4 },
];

const PhoneChat = ({ messages, botTyping, onReplay, pl }) => {
  const net = Math.max(0, pl.income - pl.ded);
  const setAside = net * 0.3;
  const income = useCountUp(pl.income);
  const ded = useCountUp(pl.ded);
  const netV = useCountUp(net);
  const set = useCountUp(setAside);
  const scrollRef = useRef(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
  }, [messages, botTyping]);

  return (
    <div className="relative">
      {/* Live P&L widget peeking from behind, left of the phone (large screens only) */}
      <div
        className="hidden lg:block absolute right-full mr-6 top-10 w-56 rounded-2xl p-4 z-0 backdrop-blur-xl bg-white/5 border border-white/10 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.6)]"
        data-testid="demo-live-pl"
      >
        <div className="flex items-center gap-2 text-[#84CC16] text-xs font-semibold uppercase tracking-wider">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} /> Live P&amp;L
        </div>
        <div className="mt-3 space-y-2.5 font-['JetBrains_Mono']">
          <Row label="Gross income" value={income} accent />
          <Row label="Deductions" value={ded} minus />
          <div className="h-px bg-white/10 my-1" />
          <Row label="Net profit" value={netV} bold />
          <Row label="Set aside ~30%" value={set} muted />
        </div>
      </div>

      {/* Phone frame */}
      <div className="relative z-10 mx-auto w-[300px] md:w-[340px] rounded-[2.2rem] border border-white/10 bg-[#04140D] p-2.5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
        <div className="rounded-[1.7rem] overflow-hidden bg-[#031009] flex flex-col h-[520px]">
          {/* chat header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-[#0A3323] border-b border-white/5">
            <div className="h-9 w-9 rounded-full bg-[#12A17A] grid place-items-center">
              <MessageCircle className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm leading-tight">MoneyFeed Bot</p>
              <p className="text-[#84CC16] text-[11px] leading-tight">online · replies instantly</p>
            </div>
          </div>
          {/* messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5" data-testid="demo-chat-window">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'} demo-msg-in`}>
                <div
                  className={`max-w-[78%] px-3.5 py-2 text-sm shadow-md ${
                    m.from === 'user'
                      ? 'bg-[#84CC16] text-black rounded-2xl rounded-br-md font-medium'
                      : 'bg-[#0A3323] text-white rounded-2xl rounded-bl-md'
                  }`}
                >
                  {m.receipt ? (
                    <span className="flex items-center gap-2">
                      <Camera className="h-4 w-4" strokeWidth={1.5} /> Receipt photo
                    </span>
                  ) : m.text}
                </div>
              </div>
            ))}
            {botTyping && (
              <div className="flex justify-start">
                <div className="bg-[#0A3323] rounded-2xl rounded-bl-md px-4 py-3 flex gap-1 items-center">
                  <span className="h-2 w-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="h-2 w-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="h-2 w-2 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
          </div>
          {/* input row */}
          <div className="px-3 py-2.5 bg-[#0A3323] border-t border-white/5 flex items-center gap-2">
            <div className="flex-1 h-9 rounded-full bg-white/5 border border-white/10 px-3 flex items-center text-white/40 text-sm">
              Type your expense…
            </div>
            <button
              onClick={onReplay}
              data-testid="demo-replay-btn"
              className="h-9 w-9 grid place-items-center rounded-full bg-[#84CC16] text-black hover:bg-lime-400 transition-colors"
              aria-label="Replay demo"
            >
              <RotateCcw className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent, minus, bold, muted }) => (
  <div className="flex items-center justify-between text-xs">
    <span className={`${muted ? 'text-white/40' : 'text-white/60'}`}>{label}</span>
    <span
      className={`tabular-nums ${bold ? 'text-white text-sm font-semibold' : ''} ${
        accent ? 'text-[#84CC16]' : minus ? 'text-white/80' : muted ? 'text-white/50' : 'text-white'
      }`}
    >
      {minus ? '−' : ''}${money(value)}
    </span>
  </div>
);

/* ---------- USP bento ---------- */
const USPS = [
  { icon: MessageCircle, title: 'Bookkeeping inside WhatsApp', desc: 'No new app to open. Text it, we log it — instantly.', span: 'md:col-span-6' },
  { icon: Sparkles, title: 'AI auto-categorization', desc: 'Every expense mapped to IRS Schedule C, VAT or income-tax lines.', span: 'md:col-span-6' },
  { icon: Camera, title: 'Snap-a-receipt', desc: 'Photograph any receipt — MoneyFeed reads the vendor, amount & category.', span: 'md:col-span-4', img: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxyZWNlaXB0cyUyMGFjY291bnRpbmclMjBmbGF0bGF5fGVufDB8fHx8MTc4NTAzMTk0Mnww&ixlib=rb-4.1.0&q=85' },
  { icon: Globe2, title: 'Multi-country & multi-currency', desc: 'US, UK, Canada, UAE, Saudi Arabia, Bahrain & Oman — with the right tax rules for each.', span: 'md:col-span-8' },
  { icon: Car, title: 'Mileage tracking', desc: 'Log trips, get tiered mileage deductions automatically.', span: 'md:col-span-4' },
  { icon: Gauge, title: 'Quarterly tax estimates', desc: 'Know what to set aside every quarter — no April surprises.', span: 'md:col-span-4' },
  { icon: Users, title: 'Team expenses', desc: 'Let teammates submit expenses straight from WhatsApp.', span: 'md:col-span-4' },
  { icon: Landmark, title: 'Bank auto-import (coming soon)', desc: 'Bank linking is planned for a future release and is not available yet. When it arrives, you will be able to connect your bank and reconcile in seconds.', span: 'md:col-span-4' },
];

const UspCard = ({ icon: Icon, title, desc, span, img }) => {
  const [ref, shown] = useReveal();
  return (
    <div
      ref={ref}
      className={`group relative overflow-hidden rounded-3xl p-6 border border-white/10 bg-[#062217] hover:-translate-y-1 hover:border-[#84CC16]/40 transition-[transform,border-color] duration-300 ${span} ${shown ? 'demo-reveal-in' : 'opacity-0'}`}
    >
      {img && (
        <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(3,16,9,.4), rgba(3,16,9,.95)), url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      )}
      <div className="relative">
        <div className="h-11 w-11 rounded-xl bg-[#0A3323] grid place-items-center text-[#84CC16] ring-1 ring-white/10">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <h3 className="mt-4 text-white font-bold text-lg font-['Cabinet_Grotesk'] tracking-tight">{title}</h3>
        <p className="mt-1.5 text-white/60 text-sm leading-relaxed">{desc}</p>
      </div>
    </div>
  );
};

/* ---------- receipt scanner demo ---------- */
const SCAN_FIELDS = [
  { icon: Building2, label: 'Vendor', value: 'Staples' },
  { icon: Calendar, label: 'Date', value: 'Mar 14, 2025' },
  { icon: Tag, label: 'Category', value: 'Office supplies' },
  { icon: FileSpreadsheet, label: 'Tax line', value: 'Schedule C · Line 22' },
];

const ReceiptScanDemo = () => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState('aim'); // aim -> flash -> scan -> extract -> done
  const [runId, setRunId] = useState(0);
  useEffect(() => {
    if (reduced) { setPhase('done'); return; }
    setPhase('aim');
    const timers = [
      setTimeout(() => setPhase('flash'), 1400),
      setTimeout(() => setPhase('scan'), 1750),
      setTimeout(() => setPhase('extract'), 3100),
      setTimeout(() => setPhase('done'), 4150),
      setTimeout(() => setRunId((x) => x + 1), 9000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [runId, reduced]);

  const captured = phase !== 'aim';
  const brackets = [
    'top-0 left-0 border-t-2 border-l-2', 'top-0 right-0 border-t-2 border-r-2',
    'bottom-0 left-0 border-b-2 border-l-2', 'bottom-0 right-0 border-b-2 border-r-2',
  ];
  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center">
      {/* camera / phone */}
      <div className="flex justify-center">
        <div className="relative w-[280px] rounded-[2rem] border border-white/10 bg-[#04140D] p-2.5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
          <div className="relative rounded-[1.5rem] overflow-hidden bg-black h-[420px]">
            <img
              src="/demo-receipt.jpg" alt="Receipt being scanned"
              className={`absolute inset-0 h-full w-full object-cover transition-all duration-500 ${captured ? 'scale-100' : 'scale-105'} ${phase === 'scan' || phase === 'extract' ? 'brightness-110' : ''}`}
            />
            {phase === 'aim' && <div className="absolute inset-0 bg-black/30" />}
            <div className="absolute inset-4 pointer-events-none">
              {brackets.map((c, i) => (
                <span key={i} className={`absolute h-6 w-6 border-[#84CC16] ${c} ${phase === 'aim' ? 'animate-pulse' : ''}`} />
              ))}
            </div>
            {phase === 'scan' && (
              <div className="absolute left-2 right-2 top-4 h-[3px] rounded bg-[#84CC16] shadow-[0_0_18px_4px_rgba(132,204,22,0.7)] demo-scanline" />
            )}
            {phase === 'flash' && <div className="absolute inset-0 bg-white demo-flash" />}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
              <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur text-white text-[11px] font-medium flex items-center gap-1.5 whitespace-nowrap">
                {phase === 'aim' && <><Camera className="h-3.5 w-3.5 text-[#84CC16]" strokeWidth={1.5} /> Point at your receipt</>}
                {phase === 'flash' && <>Captured</>}
                {phase === 'scan' && <><span className="h-1.5 w-1.5 rounded-full bg-[#84CC16] animate-pulse" /> Scanning…</>}
                {phase === 'extract' && <><Loader2 className="h-3.5 w-3.5 animate-spin text-[#84CC16]" /> Reading details…</>}
                {phase === 'done' && <><CheckCircle2 className="h-3.5 w-3.5 text-[#84CC16]" /> Added to your books</>}
              </span>
            </div>
            {phase === 'aim' && (
              <button
                onClick={() => setPhase('flash')} data-testid="demo-shutter"
                className="absolute bottom-4 left-1/2 -translate-x-1/2 h-12 w-12 rounded-full bg-white/90 ring-4 ring-white/30 hover:scale-105 transition-transform"
                aria-label="Capture"
              />
            )}
          </div>
        </div>
      </div>
      {/* extracted details */}
      <div>
        <div className="flex items-center gap-2 text-[#84CC16] text-xs font-semibold uppercase tracking-wider">
          <Receipt className="h-4 w-4" strokeWidth={1.5} /> Extracted by MoneyFeed
        </div>
        <div className="mt-4 rounded-3xl border border-white/10 bg-[#062217] p-6 min-h-[280px]" data-testid="demo-scan-card">
          {phase !== 'done' ? (
            <div className="flex items-center gap-3 text-white/40 text-sm py-16 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#84CC16]" /> Waiting for scan…
            </div>
          ) : (
            <div className="space-y-3">
              {SCAN_FIELDS.map((f, i) => (
                <div key={f.label} className="flex items-center justify-between demo-field-in" style={{ animationDelay: `${i * 90}ms` }}>
                  <span className="flex items-center gap-2 text-white/50 text-sm"><f.icon className="h-4 w-4" strokeWidth={1.5} /> {f.label}</span>
                  <span className="text-white font-medium text-sm">{f.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between demo-field-in pt-3 border-t border-white/10" style={{ animationDelay: '360ms' }}>
                <span className="text-white/50 text-sm">Amount</span>
                <span className="text-[#84CC16] font-bold text-xl font-['JetBrains_Mono']">$216.56</span>
              </div>
              <div className="demo-field-in flex items-center gap-2 pt-1 text-[#84CC16] text-sm font-medium" style={{ animationDelay: '440ms' }}>
                <CheckCircle2 className="h-4 w-4" /> Saved &amp; categorized automatically
              </div>
            </div>
          )}
        </div>
        <button onClick={() => setRunId((x) => x + 1)} data-testid="demo-scan-replay" className="mt-4 inline-flex items-center gap-2 text-white/70 hover:text-[#84CC16] text-sm transition-colors">
          <RotateCcw className="h-4 w-4" strokeWidth={1.5} /> Replay scan
        </button>
      </div>
    </div>
  );
};

/* ---------- mileage tracker demo ---------- */
const ROUTE_D = 'M36 268 C 70 210, 40 170, 96 156 S 176 150, 168 96 S 196 44, 208 30';

const MileageTrackDemo = () => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState('track'); // track -> done
  const [milesTarget, setMilesTarget] = useState(reduced ? 24.8 : 0);
  const [runId, setRunId] = useState(0);
  const miles = useCountUp(milesTarget, 2200);
  useEffect(() => {
    if (reduced) { setPhase('done'); setMilesTarget(24.8); return; }
    setPhase('track'); setMilesTarget(0);
    const t = [
      setTimeout(() => setMilesTarget(24.8), 60),
      setTimeout(() => setPhase('done'), 2500),
      setTimeout(() => setRunId((x) => x + 1), 7500),
    ];
    return () => t.forEach(clearTimeout);
  }, [runId, reduced]);

  const deduction = 24.8 * 0.76;
  const ded = useCountUp(phase === 'done' ? deduction : 0, 700);

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center">
      {/* phone map */}
      <div className="flex justify-center">
        <div className="relative w-[280px] rounded-[2rem] border border-white/10 bg-[#04140D] p-2.5 shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)]">
          <div className="relative rounded-[1.5rem] overflow-hidden bg-[#04160E] h-[420px]">
            {/* status pill */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <span className="px-3 py-1 rounded-full bg-black/60 backdrop-blur text-white text-[11px] font-medium flex items-center gap-1.5 whitespace-nowrap">
                {phase === 'track'
                  ? <><span className="h-1.5 w-1.5 rounded-full bg-[#84CC16] animate-pulse" /> Tracking trip…</>
                  : <><CheckCircle2 className="h-3.5 w-3.5 text-[#84CC16]" /> Trip logged</>}
              </span>
            </div>
            {/* miles readout */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 text-center">
              <div className="text-4xl font-black text-white font-['JetBrains_Mono'] tabular-nums leading-none">
                {miles.toFixed(1)}
              </div>
              <div className="text-[11px] uppercase tracking-widest text-[#84CC16] mt-1">miles</div>
            </div>
            {/* map */}
            <div className="absolute inset-0 grid place-items-center">
              <div className="relative" style={{ width: 240, height: 300 }}>
                <svg width="240" height="300" viewBox="0 0 240 300" fill="none" className="absolute inset-0">
                  {/* faint road grid */}
                  {[60, 120, 180].map((x) => <line key={'v' + x} x1={x} y1="0" x2={x} y2="300" stroke="#ffffff" strokeOpacity="0.05" />)}
                  {[75, 150, 225].map((y) => <line key={'h' + y} x1="0" y1={y} x2="240" y2={y} stroke="#ffffff" strokeOpacity="0.05" />)}
                  {/* base route (dim) */}
                  <path d={ROUTE_D} stroke="#ffffff" strokeOpacity="0.12" strokeWidth="5" strokeLinecap="round" />
                  {/* animated route */}
                  <path key={runId} d={ROUTE_D} pathLength="1" className="demo-route" stroke="#84CC16" strokeWidth="5" strokeLinecap="round" />
                </svg>
                {/* start / end pins */}
                <MapPin className="absolute h-6 w-6 text-white" style={{ left: 26, top: 246 }} strokeWidth={2} fill="#04160E" />
                <MapPin className="absolute h-6 w-6 text-[#84CC16]" style={{ left: 198, top: 12 }} strokeWidth={2} fill="#04160E" />
                {/* moving car */}
                <span
                  key={'car' + runId}
                  className="demo-car absolute grid place-items-center h-7 w-7 rounded-full bg-[#84CC16] text-black shadow-[0_0_18px_4px_rgba(132,204,22,0.6)]"
                  style={{ offsetPath: `path('${ROUTE_D}')`, left: -14, top: -14 }}
                >
                  <Navigation className="h-3.5 w-3.5" strokeWidth={2} fill="currentColor" />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* deduction card */}
      <div>
        <div className="flex items-center gap-2 text-[#84CC16] text-xs font-semibold uppercase tracking-wider">
          <RouteIcon className="h-4 w-4" strokeWidth={1.5} /> Auto-calculated deduction
        </div>
        <div className="mt-4 rounded-3xl border border-white/10 bg-[#062217] p-6 min-h-[280px]" data-testid="demo-mileage-card">
          {phase !== 'done' ? (
            <div className="flex items-center gap-3 text-white/40 text-sm py-16 justify-center">
              <Navigation className="h-5 w-5 text-[#84CC16] animate-pulse" /> Recording your route…
            </div>
          ) : (
            <div className="space-y-3">
              {[
                { icon: RouteIcon, label: 'Trip distance', value: '24.8 mi' },
                { icon: MapPin, label: 'Purpose', value: 'Client visit · Business' },
                { icon: Calendar, label: 'Date', value: 'Sep 14, 2026' },
                { icon: Gauge, label: 'IRS mileage rate', value: '$0.76 / mile' },
              ].map((f, i) => (
                <div key={f.label} className="flex items-center justify-between demo-field-in" style={{ animationDelay: `${i * 90}ms` }}>
                  <span className="flex items-center gap-2 text-white/50 text-sm"><f.icon className="h-4 w-4" strokeWidth={1.5} /> {f.label}</span>
                  <span className="text-white font-medium text-sm">{f.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between demo-field-in pt-3 border-t border-white/10" style={{ animationDelay: '360ms' }}>
                <span className="text-white/50 text-sm">This trip's deduction</span>
                <span className="text-[#84CC16] font-bold text-xl font-['JetBrains_Mono']">${money(ded)}</span>
              </div>
              <div className="demo-field-in flex items-center justify-between text-white/50 text-xs pt-1" style={{ animationDelay: '440ms' }}>
                <span>Year-to-date</span>
                <span className="font-['JetBrains_Mono'] text-white/70">1,240 mi · $899.00</span>
              </div>
              <div className="demo-field-in text-white/35 text-[11px] pt-1 leading-relaxed" style={{ animationDelay: '520ms' }}>
                Applies your country's rate by trip date. 2026 US IRS business rate: $0.725/mi (Jan 1–Jun 30) · $0.76/mi (from Jul 1). UK 45p then 25p tiered.
              </div>
            </div>
          )}
        </div>
        <button onClick={() => setRunId((x) => x + 1)} data-testid="demo-mileage-replay" className="mt-4 inline-flex items-center gap-2 text-white/70 hover:text-[#84CC16] text-sm transition-colors">
          <RotateCcw className="h-4 w-4" strokeWidth={1.5} /> Replay trip
        </button>
      </div>
    </div>
  );
};

/* ---------- tax estimate slider ---------- */
const TaxEstimateWidget = () => {
  const { content: c } = useCountry();
  const sym = c.sym || '$';
  const [income, setIncome] = useState(60000);
  const se = income * 0.9235 * 0.153;
  const fed = Math.max(0, (income - se / 2) * 0.12);
  const est = se + fed;
  const estV = useCountUp(est);
  return (
    <div className="rounded-3xl p-6 md:p-8 border border-white/10 backdrop-blur-xl bg-white/5 md:col-span-6">
      <p className="text-[#84CC16] text-xs font-semibold uppercase tracking-wider">Instant estimate</p>
      <h3 className="mt-2 text-white font-bold text-2xl font-['Cabinet_Grotesk'] tracking-tight">What will you owe?</h3>
      <p className="mt-4 text-white/60 text-sm">Annual net income: <span className="text-white font-['JetBrains_Mono']">{sym}{money(income)}</span></p>
      <input
        type="range" min="10000" max="250000" step="1000" value={income}
        onChange={(e) => setIncome(Number(e.target.value))}
        data-testid="demo-tax-slider"
        className="mt-3 w-full accent-[#84CC16]"
      />
      <div className="mt-5 rounded-2xl bg-[#031009] border border-white/10 p-5 flex items-end justify-between">
        <span className="text-white/60 text-sm">Est. {c.short} tax</span>
        <span className="text-[#84CC16] text-3xl font-bold font-['JetBrains_Mono']" data-testid="demo-tax-result">{sym}{money(estV)}</span>
      </div>
      <Link to={c.calcTo} className="mt-4 inline-flex items-center gap-1 text-white/70 hover:text-[#84CC16] text-sm transition-colors">
        Open full calculator <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
      </Link>
      <p className="mt-3 text-white/35 text-[11px]">Simplified estimate for illustration — not tax advice. Use the full calculator for a detailed figure.</p>
    </div>
  );
};

/* ---------- report preview ---------- */
const ReportPreview = () => {
  const { content: c } = useCountry();
  const [ref, shown] = useReveal();
  const rows = [
    ['Line 1', 'Gross receipts', '48,200.00'],
    ['Line 8', 'Advertising', '1,240.00'],
    ['Line 9', 'Car & truck', '3,180.00'],
    ['Line 24b', 'Meals (50%)', '910.00'],
    ['Line 27a', 'Other expenses', '2,455.00'],
  ];
  return (
    <div ref={ref} className={`rounded-3xl border border-white/10 bg-[#062217] p-6 md:p-8 md:col-span-6 ${shown ? 'demo-reveal-in' : 'opacity-0'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-white/70">
          <FileSpreadsheet className="h-5 w-5 text-[#84CC16]" strokeWidth={1.5} />
          <span className="text-sm font-semibold">{c.reportLabel} — 2025</span>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-white/40">CPA-ready</span>
      </div>
      <div className="mt-5 font-['JetBrains_Mono'] text-xs">
        {rows.map(([box, label, amt], i) => (
          <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-white/40 w-16">{box}</span>
            <span className="text-white/70 flex-1">{label}</span>
            <span className="text-white tabular-nums">{amt}</span>
          </div>
        ))}
        <div className="flex items-center justify-between py-3 mt-1">
          <span className="text-[#84CC16] font-semibold flex-1">Net profit (Line 31)</span>
          <span className="text-[#84CC16] font-bold tabular-nums">40,415.00</span>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        {['PDF', 'CSV', 'Excel'].map((f) => (
          <span key={f} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-white/60 text-xs">{f}</span>
        ))}
      </div>
    </div>
  );
};

/* ---------- shared browser frame ---------- */
const BrowserFrame = ({ title, children, testid }) => (
  <div
    data-testid={testid}
    className="relative w-full max-w-[420px] mx-auto rounded-2xl border border-white/10 bg-[#04140D] shadow-[0_40px_80px_-30px_rgba(0,0,0,0.8)] overflow-hidden"
  >
    <div className="flex items-center gap-2 px-4 py-2.5 bg-[#0A3323] border-b border-white/5">
      <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
      <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
      <span className="ml-3 text-white/50 text-[11px] font-medium truncate flex items-center gap-1.5">
        <LayoutGrid className="h-3 w-3 text-[#84CC16]" strokeWidth={1.5} /> {title}
      </span>
    </div>
    <div className="bg-[#031009] h-[420px] overflow-hidden">{children}</div>
  </div>
);

/* ---------- bulk scan center demo ---------- */
const SCAN_RECEIPTS = [
  { merchant: 'Staples', amount: '128.40', cat: 'Office supplies' },
  { merchant: 'Shell', amount: '52.10', cat: 'Car & truck' },
  { merchant: 'Uber', amount: '23.75', cat: 'Travel' },
  { merchant: 'Starbucks', amount: '18.90', cat: 'Meals (50%)' },
  { merchant: 'Amazon', amount: '214.30', cat: 'Supplies' },
];

const BulkScanDemo = () => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState('drop'); // drop -> scanning -> review -> done
  const [target, setTarget] = useState(reduced ? 20 : 0);
  const [runId, setRunId] = useState(0);
  const scanned = useCountUp(target, 1600);

  useEffect(() => {
    if (reduced) { setPhase('done'); setTarget(20); return; }
    setPhase('drop'); setTarget(0);
    const t = [
      setTimeout(() => { setPhase('scanning'); setTarget(20); }, 1300),
      setTimeout(() => setPhase('review'), 3300),
      setTimeout(() => setPhase('done'), 4700),
      setTimeout(() => setRunId((x) => x + 1), 10500),
    ];
    return () => t.forEach(clearTimeout);
  }, [runId, reduced]);

  const scanning = phase === 'scanning';
  const reviewing = phase === 'review' || phase === 'done';
  const pct = Math.min(100, (scanned / 20) * 100);

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center">
      {/* browser: scan center */}
      <div className="flex justify-center">
        <BrowserFrame title="MoneyFeed · Scan Center" testid="demo-scan-center-window">
          <div className="p-4 h-full flex flex-col">
            {/* status pill */}
            <div className="flex items-center justify-between">
              <span className="px-2.5 py-1 rounded-full bg-black/40 text-white text-[11px] font-medium flex items-center gap-1.5 whitespace-nowrap">
                {phase === 'drop' && <><Upload className="h-3.5 w-3.5 text-[#84CC16]" strokeWidth={1.5} /> 20 files ready</>}
                {scanning && <><Loader2 className="h-3.5 w-3.5 animate-spin text-[#84CC16]" /> Scanning {Math.round(scanned)}/20…</>}
                {reviewing && <><CheckCircle2 className="h-3.5 w-3.5 text-[#84CC16]" /> Review &amp; confirm</>}
              </span>
              <span className="text-white/30 text-[11px] font-['JetBrains_Mono']">.jpg · .pdf</span>
            </div>

            {!reviewing ? (
              <>
                {/* drop zone */}
                <div className={`mt-3 flex-1 rounded-xl border-2 border-dashed grid place-items-center transition-colors ${phase === 'drop' ? 'border-[#84CC16]/50 bg-[#84CC16]/5' : 'border-white/10'}`}>
                  <div className="grid grid-cols-5 gap-1.5 px-3">
                    {Array.from({ length: 20 }).map((_, i) => {
                      const done = scanning && (scanned / 20) * 20 > i;
                      return (
                        <div
                          key={i}
                          className={`relative h-8 w-6 rounded-[3px] border demo-field-in ${done ? 'border-[#84CC16]/60 bg-[#0A3323]' : 'border-white/10 bg-white/5'}`}
                          style={{ animationDelay: `${i * 45}ms` }}
                        >
                          <Receipt className="absolute inset-0 m-auto h-3 w-3 text-white/30" strokeWidth={1.5} />
                          {done && <CheckCircle2 className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 text-[#84CC16] bg-[#031009] rounded-full" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* progress */}
                <div className="mt-3">
                  <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-[#84CC16] rounded-full transition-[width] duration-200" style={{ width: `${phase === 'drop' ? 0 : pct}%` }} />
                  </div>
                </div>
              </>
            ) : (
              /* review table */
              <div className="mt-3 flex-1 overflow-hidden">
                <div className="grid grid-cols-[1fr_auto] gap-2 text-[10px] uppercase tracking-wider text-white/40 px-1 pb-1 border-b border-white/10">
                  <span>Merchant · Category</span><span>Amount</span>
                </div>
                {SCAN_RECEIPTS.map((r, i) => (
                  <div
                    key={r.merchant}
                    className="grid grid-cols-[auto_1fr_auto] items-center gap-2 py-2 border-b border-white/5 demo-field-in"
                    style={{ animationDelay: `${i * 90}ms` }}
                  >
                    <span className="h-4 w-4 rounded-[4px] bg-[#84CC16] grid place-items-center">
                      <Check className="h-3 w-3 text-black" strokeWidth={3} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-white text-xs font-medium truncate">{r.merchant}</span>
                      <span className="block text-white/40 text-[10px] truncate">{r.cat}</span>
                    </span>
                    <span className="text-white font-['JetBrains_Mono'] text-xs tabular-nums">${r.amount}</span>
                  </div>
                ))}
                <div className="text-white/40 text-[11px] py-2 text-center">+ 15 more receipts</div>
              </div>
            )}
          </div>
        </BrowserFrame>
      </div>

      {/* result card */}
      <div>
        <div className="flex items-center gap-2 text-[#84CC16] text-xs font-semibold uppercase tracking-wider">
          <ListChecks className="h-4 w-4" strokeWidth={1.5} /> Batch imported in one go
        </div>
        <div className="mt-4 rounded-3xl border border-white/10 bg-[#062217] p-6 min-h-[280px]" data-testid="demo-scan-center-card">
          {!reviewing ? (
            <div className="flex items-center gap-3 text-white/40 text-sm py-16 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#84CC16]" /> Scanning your pile of receipts…
            </div>
          ) : (
            <div className="space-y-3">
              {[
                { icon: Receipt, label: 'Receipts scanned', value: '20' },
                { icon: LayoutGrid, label: 'Categories detected', value: '6' },
                { icon: FileSpreadsheet, label: 'Mapped to tax lines', value: 'Automatic' },
                { icon: Gauge, label: 'Time saved', value: '~45 min' },
              ].map((f, i) => (
                <div key={f.label} className="flex items-center justify-between demo-field-in" style={{ animationDelay: `${i * 90}ms` }}>
                  <span className="flex items-center gap-2 text-white/50 text-sm"><f.icon className="h-4 w-4" strokeWidth={1.5} /> {f.label}</span>
                  <span className="text-white font-medium text-sm">{f.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between demo-field-in pt-3 border-t border-white/10" style={{ animationDelay: '360ms' }}>
                <span className="text-white/50 text-sm">Total captured</span>
                <span className="text-[#84CC16] font-bold text-xl font-['JetBrains_Mono']">$2,480.60</span>
              </div>
              <div className="demo-field-in flex items-center gap-2 pt-1 text-[#84CC16] text-sm font-medium" style={{ animationDelay: '440ms' }}>
                <CheckCircle2 className="h-4 w-4" /> 20 expenses added to your books
              </div>
            </div>
          )}
        </div>
        <button onClick={() => setRunId((x) => x + 1)} data-testid="demo-scan-center-replay" className="mt-4 inline-flex items-center gap-2 text-white/70 hover:text-[#84CC16] text-sm transition-colors">
          <RotateCcw className="h-4 w-4" strokeWidth={1.5} /> Replay bulk scan
        </button>
      </div>
    </div>
  );
};

/* ---------- export center demo ---------- */
const EXPORT_ROWS = [
  ['03/14', 'Expense', '128.40', 'Office', 'Staples'],
  ['03/12', 'Income', '1,200.00', 'Revenue', 'Acme Design'],
  ['03/10', 'Expense', '52.10', 'Car', 'Shell'],
  ['03/08', 'Expense', '23.75', 'Travel', 'Uber'],
  ['03/05', 'Expense', '18.90', 'Meals', 'Starbucks'],
];

const ExportCenterDemo = () => {
  const reduced = usePrefersReducedMotion();
  const [phase, setPhase] = useState('idle'); // idle -> building -> ready -> download
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    if (reduced) { setPhase('download'); return; }
    setPhase('idle');
    const t = [
      setTimeout(() => setPhase('building'), 1100),
      setTimeout(() => setPhase('ready'), 2600),
      setTimeout(() => setPhase('download'), 3900),
      setTimeout(() => setRunId((x) => x + 1), 10000),
    ];
    return () => t.forEach(clearTimeout);
  }, [runId, reduced]);

  const built = phase === 'ready' || phase === 'download';
  const downloaded = phase === 'download';

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center">
      {/* browser: export center */}
      <div className="flex justify-center">
        <BrowserFrame title="MoneyFeed · Export Center" testid="demo-export-center-window">
          <div className="p-4 h-full flex flex-col">
            {/* filters */}
            <div className="flex flex-wrap gap-1.5">
              {[
                { icon: Calendar, v: 'Year: 2025' },
                { icon: Filter, v: 'Type: All' },
                { icon: Tag, v: 'Category: All' },
              ].map((f) => (
                <span key={f.v} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-white/70 text-[11px] flex items-center gap-1.5">
                  <f.icon className="h-3 w-3 text-[#84CC16]" strokeWidth={1.5} /> {f.v}
                </span>
              ))}
            </div>

            {/* spreadsheet preview */}
            <div className="mt-3 flex-1 rounded-xl border border-white/10 overflow-hidden">
              <div className="grid grid-cols-[46px_1fr_64px_60px] bg-[#0A3323] text-[10px] uppercase tracking-wider text-white/60">
                {['Date', 'Vendor', 'Amount', 'Cat'].map((h) => (
                  <span key={h} className="px-2 py-1.5 border-r border-white/5 last:border-r-0 truncate">{h}</span>
                ))}
              </div>
              {!built ? (
                <div className="grid place-items-center h-[280px] text-white/40 text-xs gap-2">
                  {phase === 'building'
                    ? <><Loader2 className="h-5 w-5 animate-spin text-[#84CC16]" /> Building 1,240 rows…</>
                    : <><FileSpreadsheet className="h-5 w-5 text-[#84CC16]" /> Ready to export</>}
                </div>
              ) : (
                <div>
                  {EXPORT_ROWS.map((r, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-[46px_1fr_64px_60px] text-[11px] border-b border-white/5 demo-field-in font-['JetBrains_Mono']"
                      style={{ animationDelay: `${i * 80}ms` }}
                    >
                      <span className="px-2 py-1.5 text-white/50 border-r border-white/5 truncate">{r[0]}</span>
                      <span className="px-2 py-1.5 text-white/80 border-r border-white/5 truncate font-['Satoshi']">{r[4]}</span>
                      <span className={`px-2 py-1.5 border-r border-white/5 truncate tabular-nums ${r[1] === 'Income' ? 'text-[#84CC16]' : 'text-white/80'}`}>{r[2]}</span>
                      <span className="px-2 py-1.5 text-white/50 truncate font-['Satoshi']">{r[3]}</span>
                    </div>
                  ))}
                  <div className="text-white/40 text-[10px] py-1.5 text-center">+ 1,235 more rows</div>
                </div>
              )}
            </div>

            {/* format buttons */}
            <div className="mt-3 flex items-center gap-2">
              {[{ l: 'CSV', i: FileDown }, { l: 'Excel', i: FileSpreadsheet }].map((b, idx) => (
                <span
                  key={b.l}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors ${downloaded && idx === 1 ? 'bg-[#84CC16] text-black' : 'bg-white/5 border border-white/10 text-white/70'}`}
                >
                  {downloaded && idx === 1 ? <Download className="h-3.5 w-3.5 animate-bounce" /> : <b.i className="h-3.5 w-3.5" strokeWidth={1.5} />} {b.l}
                </span>
              ))}
            </div>
          </div>
        </BrowserFrame>
      </div>

      {/* result card */}
      <div>
        <div className="flex items-center gap-2 text-[#84CC16] text-xs font-semibold uppercase tracking-wider">
          <Table2 className="h-4 w-4" strokeWidth={1.5} /> Accountant-ready in one click
        </div>
        <div className="mt-4 rounded-3xl border border-white/10 bg-[#062217] p-6 min-h-[280px]" data-testid="demo-export-center-card">
          {!downloaded ? (
            <div className="flex items-center gap-3 text-white/40 text-sm py-16 justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-[#84CC16]" /> Preparing your export…
            </div>
          ) : (
            <div className="space-y-3">
              {[
                { icon: Table2, label: 'Rows exported', value: '1,240' },
                { icon: Calendar, label: 'Date range', value: 'Jan–Dec 2025' },
                { icon: Tag, label: 'Columns', value: 'Date · Amount · VAT · Net' },
                { icon: FileSpreadsheet, label: 'Formats', value: 'CSV · Excel' },
              ].map((f, i) => (
                <div key={f.label} className="flex items-center justify-between demo-field-in" style={{ animationDelay: `${i * 90}ms` }}>
                  <span className="flex items-center gap-2 text-white/50 text-sm"><f.icon className="h-4 w-4" strokeWidth={1.5} /> {f.label}</span>
                  <span className="text-white font-medium text-sm">{f.value}</span>
                </div>
              ))}
              <div className="flex items-center justify-between demo-field-in pt-3 border-t border-white/10" style={{ animationDelay: '360ms' }}>
                <span className="text-white/50 text-sm">File</span>
                <span className="text-[#84CC16] font-bold text-sm font-['JetBrains_Mono']">MoneyFeed_2025.xlsx</span>
              </div>
              <div className="demo-field-in flex items-center gap-2 pt-1 text-[#84CC16] text-sm font-medium" style={{ animationDelay: '440ms' }}>
                <CheckCircle2 className="h-4 w-4" /> Downloaded — hand it straight to your CPA
              </div>
            </div>
          )}
        </div>
        <button onClick={() => setRunId((x) => x + 1)} data-testid="demo-export-center-replay" className="mt-4 inline-flex items-center gap-2 text-white/70 hover:text-[#84CC16] text-sm transition-colors">
          <RotateCcw className="h-4 w-4" strokeWidth={1.5} /> Replay export
        </button>
      </div>
    </div>
  );
};

/* ---------- page ---------- */
export default function Demo() {
  const reduced = usePrefersReducedMotion();
  const [messages, setMessages] = useState([]);
  const [botTyping, setBotTyping] = useState(false);
  const [pl, setPl] = useState({ income: 0, ded: 0 });
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
    async function run() {
      setMessages([]); setBotTyping(false); setPl({ income: 0, ded: 0 });
      if (reduced) {
        setMessages(SEQUENCE);
        const fin = SEQUENCE.reduce((a, m) => ({ income: a.income + (m.income || 0), ded: a.ded + (m.ded || 0) }), { income: 0, ded: 0 });
        setPl(fin);
        return;
      }
      await sleep(500);
      for (const m of SEQUENCE) {
        if (cancelled) return;
        if (m.from === 'bot') { setBotTyping(true); await sleep(950); if (cancelled) return; setBotTyping(false); }
        else { await sleep(650); }
        if (cancelled) return;
        setMessages((prev) => [...prev, m]);
        if (m.income) setPl((p) => ({ ...p, income: p.income + m.income }));
        if (m.ded) setPl((p) => ({ ...p, ded: p.ded + m.ded }));
        await sleep(550);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [runId, reduced]);

  return (
    <div className="min-h-screen bg-[#02120C] text-white font-['Satoshi'] antialiased">
      <Seo
        title="MoneyFeed Live Demo — Try WhatsApp Bookkeeping Free"
        description="Type an expense and watch MoneyFeed do your books live — instant AI categorization, a running P&L and CPA-ready Schedule C / VAT reports. No sign-up — try the interactive demo now."
        path="/demo"
      />
      <LandingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 -left-40 h-96 w-96 rounded-full bg-[#12A17A]/20 blur-3xl" />
        <div className="pointer-events-none absolute top-20 right-0 h-80 w-80 rounded-full bg-[#84CC16]/10 blur-3xl" />
        <div className="container mx-auto px-4 pt-16 pb-24 md:pt-24 md:pb-32 grid md:grid-cols-12 gap-12 items-center relative">
          <div className="md:col-span-5 demo-reveal-in">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-[#84CC16] font-semibold">
              <span className="h-1.5 w-1.5 rounded-full bg-[#84CC16] animate-pulse" /> Live interactive demo
            </span>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.98] tracking-tighter font-['Cabinet_Grotesk']">
              Text your money.<br />
              <span className="text-[#84CC16]">We do the books.</span>
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg leading-relaxed max-w-md">
              Watch MoneyFeed turn a WhatsApp message into categorized, tax-ready books — live, below. No spreadsheets, no accounting software.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="cta-start-free">
                <Button className="rounded-full bg-[#84CC16] text-black font-bold px-6 h-12 hover:bg-lime-400 hover:-translate-y-0.5 transition-[transform,background-color]">
                  Start free on WhatsApp <MessageCircle className="h-4 w-4 ml-2" strokeWidth={2} />
                </Button>
              </a>
              <Link to="/auth" data-testid="cta-get-started">
                <Button variant="outline" className="rounded-full bg-white/5 text-white border-white/20 hover:bg-white/10 px-6 h-12">
                  Get started free
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-4 text-white/50 text-xs">
              <span className="flex items-center gap-1"><Check className="h-4 w-4 text-[#84CC16]" strokeWidth={2} /> Free plan</span>
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-[#84CC16]" strokeWidth={1.5} /> Encrypted &amp; private</span>
            </div>
          </div>
          <div className="md:col-span-7 flex justify-center lg:justify-end lg:pr-6 xl:pr-12">
            <PhoneChat messages={messages} botTyping={botTyping} pl={pl} onReplay={() => setRunId((x) => x + 1)} />
          </div>
        </div>
      </section>

      {/* RECEIPT SCANNER */}
      <section className="container mx-auto px-4 py-20 md:py-24 border-t border-white/5">
        <div className="max-w-2xl mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-[#84CC16] font-semibold">
            <Camera className="h-3.5 w-3.5" strokeWidth={1.5} /> Receipt scanning
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-black tracking-tighter font-['Cabinet_Grotesk']">Snap a receipt. We read it instantly.</h2>
          <p className="mt-3 text-white/60">Point your camera at any receipt or invoice — MoneyFeed extracts the vendor, date, amount and tax category, then files it to the right line automatically.</p>
        </div>
        <ReceiptScanDemo />
      </section>

      {/* MILEAGE TRACKER */}
      <section className="container mx-auto px-4 py-20 md:py-24 border-t border-white/5">
        <div className="max-w-2xl mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-[#84CC16] font-semibold">
            <Navigation className="h-3.5 w-3.5" strokeWidth={1.5} /> Mileage tracking
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-black tracking-tighter font-['Cabinet_Grotesk']">Log trips. Get every mile deducted.</h2>
          <p className="mt-3 text-white/60">MoneyFeed tracks your business trips and applies your country's tiered mileage rate automatically — turning drives into deductions without a logbook.</p>
        </div>
        <MileageTrackDemo />
      </section>

      {/* BULK SCAN CENTER */}
      <section className="container mx-auto px-4 py-20 md:py-24 border-t border-white/5">
        <div className="max-w-2xl mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-[#84CC16] font-semibold">
            <Upload className="h-3.5 w-3.5" strokeWidth={1.5} /> Bulk Scan Center
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-black tracking-tighter font-['Cabinet_Grotesk']">Drop a pile of receipts. Book them all at once.</h2>
          <p className="mt-3 text-white/60">Drag up to 20 receipts or invoices into the web app. MoneyFeed scans every one, auto-categorizes them, and lets you review &amp; confirm the whole batch in seconds — perfect for month-end and accountants.</p>
        </div>
        <BulkScanDemo />
      </section>

      {/* EXPORT CENTER */}
      <section className="container mx-auto px-4 py-20 md:py-24 border-t border-white/5">
        <div className="max-w-2xl mb-10 md:mb-14">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs text-[#84CC16] font-semibold">
            <Table2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Export Center
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-black tracking-tighter font-['Cabinet_Grotesk']">Filter, then export a clean CSV or Excel.</h2>
          <p className="mt-3 text-white/60">Pick a date range, type and category, and MoneyFeed builds an accountant-ready spreadsheet — with amounts, VAT/tax, net and vendor columns — that downloads as CSV or real Excel in one click.</p>
        </div>
        <ExportCenterDemo />
      </section>

      {/* USP BENTO */}
      <section className="container mx-auto px-4 py-20 md:py-28">
        <div className="max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-black tracking-tighter font-['Cabinet_Grotesk']">Everything a freelancer needs — nothing you don't.</h2>
          <p className="mt-3 text-white/60">One WhatsApp thread captures every receipt, expense and mile — and turns them into a CPA-ready packet for your books.</p>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          {USPS.map((u) => <UspCard key={u.title} {...u} />)}
          <TaxEstimateWidget />
          <ReportPreview />
        </div>
      </section>

      {/* CONTEXT STRIP */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-25"
          style={{ backgroundImage: 'linear-gradient(90deg, rgba(2,18,12,.98), rgba(2,18,12,.7)), url(https://images.unsplash.com/photo-1765648580890-732fa6d769c5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxmcmVlbGFuY2VyJTIwbW9kZXJuJTIwY2FmZXxlbnwwfHx8fDE3ODUwMzE5NDJ8MA&ixlib=rb-4.1.0&q=85)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="container mx-auto px-4 py-24 relative">
          <div className="max-w-xl">
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter font-['Cabinet_Grotesk'] leading-tight">
              Built for people too busy to do accounting.
            </h2>
            <p className="mt-4 text-white/70 text-lg">Freelancers, gig workers and small business owners in 7 countries keep their books tax-ready without ever opening a spreadsheet.</p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="mt-8 inline-block">
              <Button className="rounded-full bg-[#84CC16] text-black font-bold px-7 h-12 hover:bg-lime-400 hover:-translate-y-0.5 transition-[transform,background-color]">
                Try it free on WhatsApp <ArrowRight className="h-4 w-4 ml-2" strokeWidth={2} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10">
        <div className="container mx-auto px-4 py-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="MoneyFeed" className="h-7 w-auto" />
            <span className="font-bold text-white">MoneyFeed</span>
          </Link>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/60 justify-center">
            <Link to="/whatsapp-bookkeeping" className="hover:text-[#84CC16] transition-colors">WhatsApp Bookkeeping</Link>
            <Link to="/tools/1099-tax-calculator" className="hover:text-[#84CC16] transition-colors">Tax Calculator</Link>
            <Link to="/blog" className="hover:text-[#84CC16] transition-colors">Blog</Link>
            <Link to="/privacy" className="hover:text-[#84CC16] transition-colors">Privacy</Link>
          </nav>
          <p className="text-white/40 text-xs">© 2026 MoneyFeed. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
